import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikProvider, useFormik } from 'formik';
import { ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react';
import { Frequency, RRule, rrulestr } from 'rrule';
import * as yup from 'yup';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { MeetingItemFullInfoModal } from '@/modules/common/components/modals/MeetingItemFullInfoModal.tsx';
import { MeetingRemindersField } from '@/modules/common/components/modals/MeetingRemindersField.tsx';
import { Timezone } from '@/modules/common/constants/meeting-timezone.ts';
import {
  getNowInMeetingTimeZone,
  meetingDateBoundaryToUtcDate,
  meetingDateTimeToUtcIso,
  uiDateTimeToRecurrenceStartDate,
  uiDateTimeToUtcIso,
  utcIsoToMeetingDateTime,
} from '@/modules/common/helpers/meetingDateTime.ts';
import {
  MAX_MEETING_REMINDERS,
  defaultMeetingReminder,
  getDuplicateReminderIndexes,
  getVisibleMeetingReminders,
  isReminderInPast,
  normalizeMeetingReminder,
} from '@/modules/common/helpers/meetingReminders.ts';
import {
  ScheduleMeetingFormValues,
  getMeetingUpdateChanges,
} from '@/modules/common/helpers/meetingUpdateChanges.ts';
import {
  useCreateMeetingMutation,
  useMeetingSummaryTemplatesQuery,
  useUpdateMeetingMutation,
} from '@/modules/common/queries/meetings.ts';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { useUsersListQuery } from '@/modules/admin-dashboard/queries/users.ts';
import {
  Avatar,
  BaseModalProps,
  Button,
  CalendarInput,
  ModalContentWrapper,
  ModalProviderContext,
  RRuleComponent,
  Select,
  SelectOption,
  TextInputField,
  Toggle,
  getSelectOptionByEnum,
} from 'ui';
import {
  MeetingBodyType,
  MeetingDayOfWeek,
  MeetingRecurrencePatternType,
  MeetingRecurrenceRangeType,
  MeetingRecurrenceWeekIndex,
  MeetingReminder,
  MeetingReminderUnit,
  ScheduleMeetingAttendee,
  ScheduleMeetingRecurrence,
  ScheduleMeetingRequest,
  ScheduledMeetingListItem,
  ScheduledMeetingReminderItem,
} from 'compliance-api';

dayjs.extend(utc);

interface ScheduleMeetingAttendeeWithAvatar extends ScheduleMeetingAttendee {
  avatar_url?: string;
  first_name?: string;
  last_name?: string;
}

type ScheduledMeetingWithReminders = ScheduledMeetingListItem & {
  reminders?: ScheduledMeetingReminderItem[];
};

export const RRuleWeekDayToMeetingDayOfWeek = (value: number): MeetingDayOfWeek | null => {
  switch (value) {
    case 0:
      return MeetingDayOfWeek.Monday;
    case 1:
      return MeetingDayOfWeek.Tuesday;
    case 2:
      return MeetingDayOfWeek.Wednesday;
    case 3:
      return MeetingDayOfWeek.Thursday;
    case 4:
      return MeetingDayOfWeek.Friday;
    case 5:
      return MeetingDayOfWeek.Saturday;
    case 6:
      return MeetingDayOfWeek.Sunday;
    default:
      return null;
  }
};

export const RRuleFrequencyToMeetingRecurrencePatternType = (
  value: Frequency,
): MeetingRecurrencePatternType | null => {
  switch (value) {
    case Frequency.YEARLY:
      return MeetingRecurrencePatternType.RelativeYearly;
    case Frequency.MONTHLY:
      return MeetingRecurrencePatternType.RelativeMonthly;
    case Frequency.WEEKLY:
      return MeetingRecurrencePatternType.Weekly;
    case Frequency.DAILY:
      return MeetingRecurrencePatternType.Daily;
    default:
      return null;
  }
};

const createValidationSchema = ({
  initialMeetingStartTime,
  isEditingMeeting,
  isRecurrenceEnabled,
}: {
  initialMeetingStartTime?: string;
  isEditingMeeting: boolean;
  isRecurrenceEnabled: boolean;
}) =>
  yup
    .object<ScheduleMeetingRequest>({
      subject: yup
        .string()
        .trim()
        .required('This field is required')
        .min(1, 'This field is too short')
        .max(255, 'Maximum length 255'),
      attendees: yup
        .array()
        .of(
          yup.object<ScheduleMeetingAttendeeWithAvatar>().shape({
            email: yup.string().trim().email('Email must be a valid email').nullable(),
            user_id: yup.number().nullable(),
            name: yup.string().trim().nullable(),
          }),
        )
        .required('Attendees are required')
        .min(1, 'Attendees are required'),
      body: yup.string().trim().max(1000, 'Maximum length 1000'), //.required('This field is required').min(1, 'This field is too short'),
      is_online_meeting: yup.boolean().nullable(),
      end_time: yup
        .string()
        .trim()
        .required('This field is required')
        .test('end-time-after-start-time', 'End time must be after start time', function (value) {
          const { start_time, time_zone } = this.parent as ScheduleMeetingRequest;
          const selectedTimeZone = time_zone || Timezone['Australia/Sydney'];
          if (!value || !start_time) return true;

          return utcIsoToMeetingDateTime(value, selectedTimeZone).isAfter(
            utcIsoToMeetingDateTime(start_time, selectedTimeZone),
          );
        })
        .test(
          'max-end-time-duration',
          'End time cannot be more than 23hr 30mins after start time',
          function (value) {
            const { start_time, time_zone } = this.parent as ScheduleMeetingRequest;
            const selectedTimeZone = time_zone || Timezone['Australia/Sydney'];
            if (!value || !start_time) return true;

            const startTime = utcIsoToMeetingDateTime(start_time, selectedTimeZone);
            const endTime = utcIsoToMeetingDateTime(value, selectedTimeZone);

            return endTime.diff(startTime, 'minute') <= MAX_END_TIME_DURATION_MINUTES;
          },
        ),
      start_time: yup
        .string()
        .trim()
        .required('This field is required')
        .test(
          'start-time-not-before-now',
          'Start time cannot be earlier than the current date and time',
          function (value) {
            const { time_zone } = this.parent as ScheduleMeetingRequest;
            const selectedTimeZone = time_zone || Timezone['Australia/Sydney'];

            if (!value) return true;

            if (initialMeetingStartTime && value === initialMeetingStartTime) {
              return true;
            }

            if (isEditingMeeting && isRecurrenceEnabled) {
              return true;
            }

            return !utcIsoToMeetingDateTime(value, selectedTimeZone).isBefore(
              getNowInMeetingTimeZone(selectedTimeZone),
            );
          },
        ),
      send_invitations: yup.boolean().nullable(),
      create_ai_bot: yup.boolean().nullable(),
      bot_name: yup.string().trim().max(255, 'Maximum length 255').nullable(),
      template_id: yup.number().nullable(),
      time_zone: yup.string().trim().nullable(),
      body_type: yup.mixed<MeetingBodyType>().oneOf(Object.values(MeetingBodyType)).nullable(),
      recurrence: yup.object<ScheduleMeetingRecurrence>({
        pattern_type: yup
          .mixed<MeetingRecurrencePatternType>()
          .oneOf(Object.values(MeetingRecurrencePatternType)),
        interval: yup.number().nullable(),
        days_of_week: yup
          .array()
          .of(yup.mixed<MeetingDayOfWeek>().oneOf(Object.values(MeetingDayOfWeek)))
          .when(['pattern_type'], ([pattern_type], schema) => {
            if (
              [
                MeetingRecurrencePatternType.RelativeYearly,
                MeetingRecurrencePatternType.RelativeMonthly,
                MeetingRecurrencePatternType.Weekly,
              ].includes(pattern_type)
            ) {
              return schema.required(
                'Days of week is required for Yearly,  Monthly and Weekly pattern',
              );
            }
            return schema.nullable();
          }),
        day_of_month: yup.number().nullable(),
        month: yup.number().when(['pattern_type'], ([pattern_type], schema) => {
          if ([MeetingRecurrencePatternType.RelativeYearly].includes(pattern_type)) {
            return schema.required('Month is required for Year and month period');
          }
          return schema.nullable();
        }),
        first_day_of_week: yup
          .mixed<MeetingDayOfWeek>()
          .oneOf(Object.values(MeetingDayOfWeek))
          .nullable(),
        index: yup
          .mixed<MeetingRecurrenceWeekIndex>()
          .oneOf(Object.values(MeetingRecurrenceWeekIndex))
          .when(['pattern_type'], ([pattern_type], schema) => {
            if (
              [
                MeetingRecurrencePatternType.RelativeYearly,
                MeetingRecurrencePatternType.RelativeMonthly,
              ].includes(pattern_type)
            ) {
              return schema.required('Index is required for Year and month period');
            }
            return schema.nullable();
          }),

        range_type: yup
          .mixed<MeetingRecurrenceRangeType>()
          .oneOf(Object.values(MeetingRecurrenceRangeType))
          .nullable(),
        number_of_occurrences: yup.number().nullable(),
        end_date: yup.string().trim().nullable(),
        start_date: yup.string().trim().nullable(),
        location: yup.string().trim().nullable(),
      }),
      reminders: yup
        .array()
        .of(
          yup.object<MeetingReminder>().shape({
            value: yup
              .number()
              .typeError('Value must be a number')
              .integer('Value must be an integer')
              .min(1, 'Value must be greater than 0')
              .required('Value is required'),
            unit: yup
              .mixed<MeetingReminderUnit>()
              .oneOf(Object.values(MeetingReminderUnit))
              .required('Unit is required'),
          }),
        )
        .max(MAX_MEETING_REMINDERS, `Maximum ${MAX_MEETING_REMINDERS} reminders allowed`)
        .nullable(),
    })
    .test('reminders-unique', function (values) {
      const reminders = (values as ScheduleMeetingRequest | undefined)?.reminders;
      if (!reminders?.length) return true;

      const duplicateIndexes = getDuplicateReminderIndexes(reminders);
      if (!duplicateIndexes.length) return true;

      const duplicateIndex = duplicateIndexes[duplicateIndexes.length - 1];
      return this.createError({
        path: `reminders.${duplicateIndex}.value`,
        message: 'Duplicate reminder',
      });
    })
    .test('reminders-not-in-past', function (values) {
      const formValues = values as ScheduleMeetingRequest | undefined;
      const reminders = formValues?.reminders;
      const startTime = formValues?.start_time;
      const selectedTimeZone = formValues?.time_zone || Timezone['Australia/Sydney'];

      if (!reminders?.length || !startTime || isRecurrenceEnabled) return true;

      for (const [index, reminder] of reminders.entries()) {
        if (
          reminder.unit &&
          isReminderInPast({ reminder, startTime, timeZone: selectedTimeZone })
        ) {
          return this.createError({
            path: `reminders.${index}.value`,
            message: 'Reminder time cannot be in the past',
          });
        }
      }

      return true;
    })
    .test(
      'recurrence-start-date-not-before-start-time',
      'Recurrence start date cannot be earlier than meeting date',
      function (values) {
        const formValues = values as ScheduleMeetingRequest | undefined;
        const recurrence = formValues?.recurrence;
        const startTime = formValues?.start_time;
        const selectedTimeZone = formValues?.time_zone || Timezone['Australia/Sydney'];

        if (!recurrence?.start_date || !startTime) return true;
        const meetingDate = utcIsoToMeetingDateTime(startTime, selectedTimeZone).format(
          'YYYY-MM-DD',
        );

        if (recurrence.start_date < meetingDate) {
          return this.createError({
            path: 'recurrence.start_date',
            message: 'Recurrence start date cannot be earlier than meeting date',
          });
        }

        return true;
      },
    )
    .test(
      'recurrence-end-date-not-before-start-time',
      'Recurrence end date cannot be earlier than meeting date',
      function (values) {
        const formValues = values as ScheduleMeetingRequest | undefined;
        const recurrence = formValues?.recurrence;
        const startTime = formValues?.start_time;
        const selectedTimeZone = formValues?.time_zone || Timezone['Australia/Sydney'];

        if (
          recurrence?.range_type !== MeetingRecurrenceRangeType.EndDate ||
          !recurrence?.end_date ||
          !startTime
        ) {
          return true;
        }

        const meetingDate = utcIsoToMeetingDateTime(startTime, selectedTimeZone).format(
          'YYYY-MM-DD',
        );

        if (recurrence.end_date < meetingDate) {
          return this.createError({
            path: 'recurrence.end_date',
            message: 'Recurrence end date cannot be earlier than meeting date',
          });
        }

        return true;
      },
    )
    .test(
      'recurrence-end-date-not-before-recurrence-start-date',
      'Recurrence end date cannot be earlier than recurrence start date',
      function (values) {
        const formValues = values as ScheduleMeetingRequest | undefined;
        const recurrence = formValues?.recurrence;

        if (
          recurrence?.range_type !== MeetingRecurrenceRangeType.EndDate ||
          !recurrence?.end_date ||
          !recurrence?.start_date
        ) {
          return true;
        }

        if (recurrence.end_date < recurrence.start_date) {
          return this.createError({
            path: 'recurrence.end_date',
            message: 'Recurrence end date cannot be earlier than recurrence start date',
          });
        }

        return true;
      },
    );

export interface AddUpdateMeetingModalProps extends BaseModalProps {
  item?: ScheduledMeetingListItem;
  skipOpenDetailsOnSuccess?: boolean;
}

const DEFAULT_RECURRENCY_RULE = new RRule({
  freq: Frequency.DAILY,
  interval: 1,
}).toString();

const START_TIME_STEP_MINUTES = 15;
const END_TIME_SHORT_STEP_MINUTES = 15;
const END_TIME_LONG_STEP_MINUTES = 30;
const FIRST_TIME_OPTION_MINUTES = 0;
const LAST_START_TIME_OPTION_MINUTES = 23 * 60 + 45;
const MAX_END_TIME_DURATION_MINUTES = 23 * 60 + 30;

const formatTimeOptionLabel = (totalMinutes: number) =>
  dayjs().startOf('day').add(totalMinutes, 'minute').format('h:mma');

const minutesToTimeValue = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const timeValueToMinutes = (value?: string | null) => {
  if (!value) return null;

  const [hoursString, minutesString] = value.split(':');
  const hours = Number(hoursString);
  const minutes = Number(minutesString);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const minutesToSelectValue = (totalMinutes: number) => String(totalMinutes);

const selectValueToMinutes = (value?: string | null) => {
  if (!value) return null;

  const minutes = Number(value);

  if (Number.isNaN(minutes) || minutes < 0) {
    return null;
  }

  return minutes;
};

const minuteCountToDurationLabel = (totalMinutes: number) => {
  if (totalMinutes < 60) {
    return `${totalMinutes} mins`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }

  if (minutes === 30) {
    return `${hours}.5 hrs`;
  }

  return `${hours} hrs ${minutes} mins`;
};

const buildTimeOptionLabel = ({
  datePart,
  totalMinutes,
  timeZone,
  durationLabel,
}: {
  datePart: Dayjs;
  totalMinutes: number;
  timeZone: string;
  durationLabel?: string;
}): ReactNode => {
  const meetingTimeLabel = durationLabel
    ? `${formatTimeOptionLabel(totalMinutes)} (${durationLabel})`
    : formatTimeOptionLabel(totalMinutes);
  const meetingDateTime = datePart.startOf('day').add(totalMinutes, 'minute');
  const utcIso = meetingDateTimeToUtcIso(meetingDateTime, timeZone);
  const localDateTime = dayjs(utcIso);
  const zonedMeetingDateTime = utcIsoToMeetingDateTime(utcIso, timeZone);
  const isLocalTimeDifferent =
    localDateTime.format('DD-MM-YYYY HH:mm') !== zonedMeetingDateTime.format('DD-MM-YYYY HH:mm');

  if (!isLocalTimeDifferent) {
    return <span className="font-medium text-gray-900">{meetingTimeLabel}</span>;
  }

  // const localOffsetLabel = formatUtcOffsetLabel(localDateTime.utcOffset());
  const localTimeLabel = localDateTime.isSame(zonedMeetingDateTime, 'day')
    ? `${localDateTime.format('h:mma')}` // (${localOffsetLabel})
    : `${localDateTime.format('DD-MM-YYYY, h:mma')}`; // (${localOffsetLabel});

  return (
    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-medium text-gray-900">{meetingTimeLabel}</span>
      <span className="text-xs text-gray-500">Local: {localTimeLabel}</span>
    </span>
  );
};

const buildStartTimeOptions = (
  minimumMinutes = FIRST_TIME_OPTION_MINUTES,
  buildLabel: (totalMinutes: number) => ReactNode,
) => {
  const normalizedMinimum = Math.max(
    FIRST_TIME_OPTION_MINUTES,
    Math.ceil(minimumMinutes / START_TIME_STEP_MINUTES) * START_TIME_STEP_MINUTES,
  );

  const options: SelectOption<string, ReactNode>[] = [];

  for (
    let totalMinutes = normalizedMinimum;
    totalMinutes <= LAST_START_TIME_OPTION_MINUTES;
    totalMinutes += START_TIME_STEP_MINUTES
  ) {
    options.push({
      value: minutesToTimeValue(totalMinutes),
      label: buildLabel(totalMinutes),
    });
  }

  return options;
};

const roundMinutesUpToStep = (totalMinutes: number, stepMinutes: number) =>
  Math.ceil(totalMinutes / stepMinutes) * stepMinutes;

const buildEndTimeOptions = (
  startMinutes: number,
  buildLabel: (totalMinutes: number, durationLabel: string) => ReactNode,
) => {
  const options: SelectOption<string, ReactNode>[] = [];
  const durationOptions = [15, 30, 45, 60];

  for (
    let durationMinutes = 90;
    durationMinutes <= MAX_END_TIME_DURATION_MINUTES;
    durationMinutes += END_TIME_LONG_STEP_MINUTES
  ) {
    durationOptions.push(durationMinutes);
  }

  for (const durationMinutes of durationOptions) {
    const totalMinutes = startMinutes + durationMinutes;
    const durationLabel = minuteCountToDurationLabel(durationMinutes);
    options.push({
      value: minutesToSelectValue(totalMinutes),
      label: buildLabel(totalMinutes, durationLabel),
    });
  }

  return options;
};

const getValidEndDurationMinutes = () => {
  const durationOptions = [15, 30, 45, 60];

  for (
    let durationMinutes = 90;
    durationMinutes <= MAX_END_TIME_DURATION_MINUTES;
    durationMinutes += END_TIME_LONG_STEP_MINUTES
  ) {
    durationOptions.push(durationMinutes);
  }

  return durationOptions;
};

const getNearestValidEndDurationMinutes = (targetDurationMinutes: number) => {
  const validDurations = getValidEndDurationMinutes();

  return validDurations.reduce((nearestDuration, currentDuration) => {
    const currentDistance = Math.abs(currentDuration - targetDurationMinutes);
    const nearestDistance = Math.abs(nearestDuration - targetDurationMinutes);

    if (currentDistance < nearestDistance) {
      return currentDuration;
    }

    if (currentDistance === nearestDistance && currentDuration > nearestDuration) {
      return currentDuration;
    }

    return nearestDuration;
  }, validDurations[0]);
};

const appendTimeOptionIfMissing = <TLabel,>(
  options: SelectOption<string, TLabel>[],
  totalMinutes: number | null,
  buildLabel: (minutes: number) => TLabel,
) => {
  if (totalMinutes === null) return options;

  const nextValue = minutesToTimeValue(totalMinutes);
  if (options.some(option => option.value === nextValue)) {
    return options;
  }

  return [
    ...options,
    {
      value: nextValue,
      label: buildLabel(totalMinutes),
    },
  ].sort((left, right) => {
    const leftNumericValue = Number(left.value);
    const rightNumericValue = Number(right.value);

    if (!Number.isNaN(leftNumericValue) && !Number.isNaN(rightNumericValue)) {
      return leftNumericValue - rightNumericValue;
    }

    return left.value > right.value ? 1 : -1;
  });
};

const formatUtcOffsetLabel = (totalOffsetMinutes: number) => {
  const sign = totalOffsetMinutes >= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(totalOffsetMinutes);
  const hours = Math.floor(absoluteOffsetMinutes / 60);
  const minutes = absoluteOffsetMinutes % 60;

  return `GMT${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatTimeZoneOffsetLabel = (timeZone: string) =>
  formatUtcOffsetLabel(getNowInMeetingTimeZone(timeZone).utcOffset());

const dayOfWeekToRRule = (value: MeetingDayOfWeek) => {
  switch (value) {
    case MeetingDayOfWeek.Monday:
      return RRule.MO;
    case MeetingDayOfWeek.Tuesday:
      return RRule.TU;
    case MeetingDayOfWeek.Wednesday:
      return RRule.WE;
    case MeetingDayOfWeek.Thursday:
      return RRule.TH;
    case MeetingDayOfWeek.Friday:
      return RRule.FR;
    case MeetingDayOfWeek.Saturday:
      return RRule.SA;
    case MeetingDayOfWeek.Sunday:
      return RRule.SU;
    default:
      return null;
  }
};

const weekIndexToBySetPos = (value?: MeetingRecurrenceWeekIndex) => {
  switch (value) {
    case MeetingRecurrenceWeekIndex.First:
      return 1;
    case MeetingRecurrenceWeekIndex.Second:
      return 2;
    case MeetingRecurrenceWeekIndex.Third:
      return 3;
    case MeetingRecurrenceWeekIndex.Fourth:
      return 4;
    case MeetingRecurrenceWeekIndex.Last:
      return -1;
    default:
      return undefined;
  }
};

const recurrenceObjectToRRule = (
  recurrence: ScheduleMeetingRecurrence,
  startTime?: string,
  timeZone?: string,
): string => {
  const selectedTimeZone = timeZone || Timezone['Australia/Sydney'];
  const options: Record<string, any> = {
    interval: recurrence.interval ?? 1,
    wkst: dayOfWeekToRRule(recurrence.first_day_of_week || MeetingDayOfWeek.Monday) || RRule.MO,
  };

  switch (recurrence.pattern_type) {
    case MeetingRecurrencePatternType.Daily:
      options.freq = Frequency.DAILY;
      break;
    case MeetingRecurrencePatternType.Weekly:
      options.freq = Frequency.WEEKLY;
      options.byweekday =
        recurrence.days_of_week
          ?.map(dayOfWeekToRRule)
          .filter((day): day is NonNullable<typeof day> => !!day) || undefined;
      break;
    case MeetingRecurrencePatternType.AbsoluteMonthly:
      options.freq = Frequency.MONTHLY;
      options.bymonthday = recurrence.day_of_month;
      break;
    case MeetingRecurrencePatternType.RelativeMonthly:
      options.freq = Frequency.MONTHLY;
      options.bysetpos = weekIndexToBySetPos(recurrence.index);
      options.byweekday =
        recurrence.days_of_week
          ?.map(dayOfWeekToRRule)
          .filter((day): day is NonNullable<typeof day> => !!day) || undefined;
      break;
    case MeetingRecurrencePatternType.AbsoluteYearly:
      options.freq = Frequency.YEARLY;
      options.bymonth = recurrence.month;
      options.bymonthday = recurrence.day_of_month;
      break;
    case MeetingRecurrencePatternType.RelativeYearly:
      options.freq = Frequency.YEARLY;
      options.bymonth = recurrence.month;
      options.bysetpos = weekIndexToBySetPos(recurrence.index);
      options.byweekday =
        recurrence.days_of_week
          ?.map(dayOfWeekToRRule)
          .filter((day): day is NonNullable<typeof day> => !!day) || undefined;
      break;
    default:
      options.freq = Frequency.DAILY;
  }

  if (startTime) {
    options.dtstart = dayjs(startTime).toDate();
  } else if (recurrence.start_date) {
    options.dtstart = meetingDateBoundaryToUtcDate(
      recurrence.start_date,
      selectedTimeZone,
      'start',
    );
  }

  if (recurrence.range_type === MeetingRecurrenceRangeType.EndDate && recurrence.end_date) {
    options.until = meetingDateBoundaryToUtcDate(recurrence.end_date, selectedTimeZone, 'end');
  }

  if (
    recurrence.range_type === MeetingRecurrenceRangeType.Numbered &&
    recurrence.number_of_occurrences
  ) {
    options.count = recurrence.number_of_occurrences;
  }

  return new RRule(options).toString();
};

const resolveRecurrencyRule = (
  recurrence?: unknown,
  startTime?: string,
  timeZone?: string,
): string => {
  if (typeof recurrence === 'string' && recurrence.trim()) {
    return recurrence;
  }
  if (recurrence && typeof recurrence === 'object') {
    return recurrenceObjectToRRule(
      recurrence as ScheduleMeetingRecurrence,
      startTime,
      timeZone || Timezone['Australia/Sydney'],
    );
  }
  return DEFAULT_RECURRENCY_RULE;
};

export const AddUpdateMeetingModal = ({
  open,
  onClose,
  item,
  skipOpenDetailsOnSuccess = false,
}: AddUpdateMeetingModalProps) => {
  const meetingItem = item as ScheduledMeetingWithReminders | undefined;
  const isRecurringMeeting = Boolean(item?.recurrence);
  const { openModal } = useContext(ModalProviderContext);
  const { data: currentUser } = useCurrentUserQuery();
  const [isRepeat, setIsRepeat] = useState(!!item?.recurrence);
  const [didChangeReminders, setDidChangeReminders] = useState(false);
  const [reCurrencyRule, setReCurrencyRule] = useState(
    resolveRecurrencyRule(item?.recurrence, item?.start_time, item?.time_zone),
  );

  const { data: templatesResponse } = useMeetingSummaryTemplatesQuery();

  const templateOptions = useMemo(
    () =>
      (templatesResponse?.data || []).map(template => ({
        value: template.id,
        label: template.name,
      })),
    [templatesResponse?.data],
  );

  const validationSchema = useMemo(
    () =>
      createValidationSchema({
        initialMeetingStartTime: item?.start_time,
        isEditingMeeting: Boolean(item),
        isRecurrenceEnabled: isRepeat,
      }),
    [item?.start_time, item?.id, isRepeat],
  );

  const visibleMeetingReminders = useMemo(
    () =>
      getVisibleMeetingReminders({
        reminders: meetingItem?.reminders?.map(normalizeMeetingReminder) ?? [],
        startTime: item?.start_time,
        timeZone: item?.time_zone,
        isRecurring: isRecurringMeeting,
      }),
    [isRecurringMeeting, item?.start_time, item?.time_zone, meetingItem?.reminders],
  );

  const getEffectiveFormValues = useCallback(
    (values: ScheduleMeetingFormValues): ScheduleMeetingFormValues => ({
      ...values,
      recurrence: isRepeat ? values.recurrence : undefined,
      create_ai_bot: values.is_online_meeting ? values.create_ai_bot : false,
    }),
    [isRepeat],
  );

  const formik = useFormik<ScheduleMeetingFormValues>({
    validationSchema,
    initialValues: {
      subject: item?.subject ?? '',
      attendees: [
        ...(item?.attendee_users?.map<ScheduleMeetingAttendeeWithAvatar>(user => ({
          first_name: user?.first_name,
          last_name: user?.last_name,
          avatar_url: user?.avatar_url,
          name: `${user.first_name} ${user.last_name}`,
          ...(user.id ? { user_id: user.id } : user.email ? { email: user.email } : {}),
        })) || []),
        ...(item?.external_attendee_emails?.map<ScheduleMeetingAttendeeWithAvatar>(user => ({
          name: user,
          email: user,
        })) || []),
      ],
      body: item?.body ?? '',
      is_online_meeting: item?.is_online_meeting ?? true,
      end_time: item?.end_time ?? '',
      start_time: item?.start_time ?? '',
      send_invitations: item?.send_invitations ?? true,
      create_ai_bot: item?.create_ai_bot ?? false,
      bot_name: item?.bot_name ?? 'Compli AI',
      template_id: item?.template_id,
      time_zone: item?.time_zone || Timezone['Australia/Sydney'],
      body_type: MeetingBodyType.Text,
      recurrence: item?.recurrence ? (item?.recurrence as ScheduleMeetingRecurrence) : undefined,
      location: item?.location ?? '',
      reminders: visibleMeetingReminders,
      notify_attendees: true,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      const effectiveValues = getEffectiveFormValues(values);
      const meetingChanges = getMeetingUpdateChanges(item, effectiveValues);

      const normalizedValues: ScheduleMeetingFormValues = {
        ...effectiveValues,
        time_zone: values.time_zone || Timezone['Australia/Sydney'],
        notify_attendees: meetingChanges.isNotifyForced
          ? true
          : meetingChanges.canChooseNotifyAttendees
          ? values.notify_attendees
          : undefined,
        reminders: didChangeReminders
          ? values.reminders?.length
            ? values.reminders
            : []
          : undefined,
      };

      if (item) {
        await mutateAsyncUpdate({
          ...normalizedValues,
          recurrence: isRepeat ? normalizedValues.recurrence : (null as any),
          meetingId: item.id,
        });
      } else {
        await mutateAsyncAdd(normalizedValues);
      }
      formik.resetForm();
    },
  });

  const meetingChanges = useMemo(
    () => getMeetingUpdateChanges(item, getEffectiveFormValues(formik.values)),
    [item, formik.values, isRepeat, getEffectiveFormValues],
  );
  const isUpdateMode = !!item;
  const isNotifyForced = isUpdateMode && meetingChanges.isNotifyForced;
  const shouldShowForcedUpdateNotice = isNotifyForced && !meetingChanges.hasMeetingTypeChanges;
  const shouldShowNotifyAttendeesToggle = isUpdateMode && meetingChanges.canChooseNotifyAttendees;
  const shouldShowUpdateNotice = shouldShowForcedUpdateNotice || shouldShowNotifyAttendeesToggle;
  const updateNoticeText = shouldShowForcedUpdateNotice
    ? formik.values.is_online_meeting
      ? 'Attendees will be notified by email because the meeting time, recurrence, or attendee list changed. RSVP statuses may be updated or reset.'
      : 'Attendees will be notified by email because the meeting time, recurrence, or attendee list changed.'
    : formik.values.notify_attendees
    ? formik.values.is_online_meeting
      ? 'Attendees will be notified by email. RSVP statuses may be updated.'
      : 'Attendees will be notified by email.'
    : formik.values.is_online_meeting
    ? 'Attendees may not see these changes in Google Calendar for this meeting.'
    : 'Attendees will not be notified by email.';

  const selectedTimeZone = formik.values.time_zone || Timezone['Australia/Sydney'];
  const selectedStartDateTime = useMemo(
    () =>
      formik.values.start_time
        ? utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone)
        : null,
    [formik.values.start_time, selectedTimeZone],
  );
  const selectedEndDateTime = useMemo(
    () =>
      formik.values.end_time
        ? utcIsoToMeetingDateTime(formik.values.end_time, selectedTimeZone)
        : null,
    [formik.values.end_time, selectedTimeZone],
  );
  const startTimeValue = useMemo(
    () =>
      selectedStartDateTime
        ? minutesToTimeValue(selectedStartDateTime.hour() * 60 + selectedStartDateTime.minute())
        : null,
    [selectedStartDateTime],
  );
  const endTimeValue = useMemo(
    () =>
      selectedEndDateTime && selectedStartDateTime
        ? minutesToSelectValue(
            selectedEndDateTime.diff(selectedStartDateTime.startOf('day'), 'minute'),
          )
        : null,
    [selectedEndDateTime, selectedStartDateTime],
  );
  const startTimeOptions = useMemo(() => {
    const now = getNowInMeetingTimeZone(selectedTimeZone);
    const selectedDate = selectedStartDateTime || now;
    const currentStartMinutes =
      selectedStartDateTime !== null
        ? selectedStartDateTime.hour() * 60 + selectedStartDateTime.minute()
        : null;
    const shouldAllowPastStartTime =
      (item?.start_time && formik.values.start_time === item.start_time) ||
      (Boolean(item) && isRepeat);
    const minimumMinutes =
      selectedDate.isSame(now, 'day') && !shouldAllowPastStartTime
        ? now.hour() * 60 + now.minute()
        : FIRST_TIME_OPTION_MINUTES;

    return appendTimeOptionIfMissing(
      buildStartTimeOptions(minimumMinutes, minutes =>
        buildTimeOptionLabel({
          datePart: selectedDate,
          totalMinutes: minutes,
          timeZone: selectedTimeZone,
        }),
      ),
      currentStartMinutes,
      minutes =>
        buildTimeOptionLabel({
          datePart: selectedDate,
          totalMinutes: minutes,
          timeZone: selectedTimeZone,
        }),
    );
  }, [formik.values.start_time, isRepeat, item, selectedStartDateTime, selectedTimeZone]);
  const endTimeOptions = useMemo(() => {
    const startMinutes = timeValueToMinutes(startTimeValue);
    const optionDate = selectedStartDateTime || getNowInMeetingTimeZone(selectedTimeZone);

    if (startMinutes === null) {
      return [];
    }

    return buildEndTimeOptions(startMinutes, (minutes, durationLabel) =>
      buildTimeOptionLabel({
        datePart: optionDate,
        totalMinutes: minutes,
        timeZone: selectedTimeZone,
        durationLabel,
      }),
    );
  }, [selectedStartDateTime, selectedTimeZone, startTimeValue]);
  const timezoneOptions = useMemo(
    () =>
      getSelectOptionByEnum<Timezone>(Timezone, true, false).map(option => ({
        ...option,
        label: `${option.label} (${formatTimeZoneOffsetLabel(String(option.value))})`,
      })),
    [],
  );
  const localDateTimeHint = useMemo(() => {
    if (!formik.values.start_time) return null;

    const sydneyStart = utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone);
    const localStart = dayjs(formik.values.start_time);
    const localOffsetLabel = formatUtcOffsetLabel(localStart.utcOffset());

    const sydneyEnd = formik.values.end_time
      ? utcIsoToMeetingDateTime(formik.values.end_time, selectedTimeZone)
      : null;
    const localEnd = formik.values.end_time ? dayjs(formik.values.end_time) : null;

    const isStartSame =
      localStart.format('DD-MM-YYYY HH:mm') === sydneyStart.format('DD-MM-YYYY HH:mm');
    const isEndSame =
      !localEnd || !sydneyEnd
        ? true
        : localEnd.format('DD-MM-YYYY HH:mm') === sydneyEnd.format('DD-MM-YYYY HH:mm');

    if (isStartSame && isEndSame) return null;

    if (localEnd) {
      const endTimeLabel = localEnd.isSame(localStart, 'day')
        ? localEnd.format('HH:mm')
        : localEnd.format('DD-MM-YYYY, HH:mm');

      return `Local time (${localOffsetLabel}): ${localStart.format(
        'DD-MM-YYYY, HH:mm',
      )} - ${endTimeLabel}`;
    }

    return `Local time (${localOffsetLabel}): ${localStart.format('DD-MM-YYYY, HH:mm')}`;
  }, [formik.values.start_time, formik.values.end_time, selectedTimeZone]);

  const localRecurrenceHint = useMemo(() => {
    if (!isRepeat || !formik.values.recurrence?.start_date) return null;

    const sydneyStartDate = dayjs(formik.values.recurrence.start_date, 'YYYY-MM-DD').format(
      'DD-MM-YYYY',
    );
    const localStartDate = dayjs(
      meetingDateBoundaryToUtcDate(formik.values.recurrence.start_date, selectedTimeZone, 'start'),
    ).format('DD-MM-YYYY');

    const endDate = formik.values.recurrence.end_date;
    const sydneyEndDate = endDate ? dayjs(endDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : null;
    const localEndDate = endDate
      ? dayjs(meetingDateBoundaryToUtcDate(endDate, selectedTimeZone, 'start')).format('DD-MM-YYYY')
      : null;

    const hasDiffStart = localStartDate !== sydneyStartDate;
    const hasDiffEnd =
      !!endDate && !!localEndDate && !!sydneyEndDate && localEndDate !== sydneyEndDate;

    if (!hasDiffStart && !hasDiffEnd) return null;

    if (localEndDate && endDate) {
      return `Local recurrence dates: ${localStartDate} - ${localEndDate}`;
    }

    return `Local recurrence start: ${localStartDate}`;
  }, [
    isRepeat,
    formik.values.recurrence?.start_date,
    formik.values.recurrence?.end_date,
    selectedTimeZone,
  ]);

  useEffect(() => {
    setIsRepeat(!!item?.recurrence);
    setDidChangeReminders(false);
    setReCurrencyRule(resolveRecurrencyRule(item?.recurrence, item?.start_time, item?.time_zone));
  }, [item?.recurrence, item?.start_time, item?.time_zone]);

  useEffect(() => {
    if (formik.values.template_id || !templateOptions.length) return;

    const defaultTemplate =
      templateOptions.find(
        option =>
          option.label.toString().toLowerCase().includes('configurable') ||
          option.label.toString().toLowerCase().includes('detailed'),
      ) || templateOptions[0];

    if (!defaultTemplate) return;

    formik.setFieldValue('template_id', defaultTemplate.value);
    formik.setFieldError('template_id', '');
  }, [formik.values.template_id, templateOptions]);

  const handleOnSuccess = useCallback(
    async (data: { id: number }) => {
      if (!skipOpenDetailsOnSuccess) {
        openModal(MeetingItemFullInfoModal, {
          id: data.id,
        });
      }
      onClose();
    },
    [onClose, openModal, skipOpenDetailsOnSuccess],
  );

  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdate } = useUpdateMeetingMutation({
    setErrors: formik.setErrors,
    onSuccess: handleOnSuccess,
  });

  const { mutateAsync: mutateAsyncAdd, isLoading: isLoadingAdd } = useCreateMeetingMutation({
    setErrors: formik.setErrors,
    onSuccess: handleOnSuccess,
  });

  const { data: dataUsers } = useUsersListQuery(
    {
      page: 1,
      page_size: 15,
      filter_by_name: '',
    },
    { cacheKey: useId() },
  );

  useEffect(() => {
    const users = dataUsers?.pages?.at(-1)?.data || [];
    const currentUsers = formik.values.attendees as ScheduleMeetingAttendeeWithAvatar[];

    if (currentUser?.user?.id && users.length && !item) {
      const isHave = !!currentUsers.find(item => item.user_id === currentUser?.user?.id);

      if (!isHave) {
        currentUsers.push({
          user_id: currentUser?.user?.id,
          name: `${currentUser?.user?.first_name ?? ''} ${
            currentUser?.user?.last_name ?? ''
          }`.trim(),
          avatar_url: currentUser?.user?.avatar_url,
          first_name: currentUser?.user?.first_name,
          last_name: currentUser?.user?.last_name,
        });

        formik.setFieldValue('attendees', currentUsers);
        formik.setFieldError('attendees', '');
      }
    }
  }, [dataUsers, currentUser?.user, item]);

  const handleChangeSelect = (field: string) => (option: string | boolean | null | any) => {
    formik.setFieldValue(field, option);
    formik.setFieldError(field, '');
  };

  const disabledDateByMeetingTimeZone = useCallback(
    (date: Dayjs) => {
      if (!date) return false;
      const selectedDate = date.format('YYYY-MM-DD');
      const meetingToday = getNowInMeetingTimeZone(selectedTimeZone).format('YYYY-MM-DD');
      return selectedDate < meetingToday;
    },
    [selectedTimeZone],
  );

  const syncRecurrenceStartDate = useCallback(
    (
      datePart: Dayjs,
      timePart: Dayjs,
      shouldSync = Boolean(formik.values.recurrence?.start_date),
    ) => {
      if (!shouldSync) return;

      const recurrenceStartDate = uiDateTimeToRecurrenceStartDate({
        datePart,
        timePart,
        timeZone: selectedTimeZone,
      });
      const recurrenceStartTimeUtc = uiDateTimeToUtcIso({
        datePart,
        timePart,
        timeZone: selectedTimeZone,
      });

      formik.setFieldValue('recurrence.start_date', recurrenceStartDate);
      formik.setFieldError('recurrence.start_date', '');

      if (!reCurrencyRule) return;

      try {
        const currentRule = rrulestr(reCurrencyRule);
        const nextRule = new RRule({
          ...currentRule.options,
          dtstart: dayjs(recurrenceStartTimeUtc).toDate(),
        }).toString();
        setReCurrencyRule(nextRule);
      } catch {
        // Ignore invalid rule and keep current fallback behaviour.
      }
    },
    [formik.values.recurrence?.start_date, reCurrencyRule, selectedTimeZone],
  );

  const handleChangeDate = useCallback<(date: Dayjs, dateString: string | string[]) => void>(
    date => {
      if (!date) {
        formik.setFieldValue('start_time', null);
        formik.setFieldError('start_time', '');
        formik.setFieldValue('end_time', null);
        formik.setFieldError('end_time', '');
        return;
      }

      const nowInMeetingTimeZone = getNowInMeetingTimeZone(selectedTimeZone);
      const roundedNowMinutes = roundMinutesUpToStep(
        nowInMeetingTimeZone.hour() * 60 + nowInMeetingTimeZone.minute(),
        START_TIME_STEP_MINUTES,
      );
      const roundedNow = nowInMeetingTimeZone
        .startOf('day')
        .add(Math.min(roundedNowMinutes, LAST_START_TIME_OPTION_MINUTES), 'minute');
      const oldStart = formik.values.start_time
        ? utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone)
        : roundedNow;
      const oldEnd = formik.values.end_time
        ? utcIsoToMeetingDateTime(formik.values.end_time, selectedTimeZone)
        : oldStart.add(1, 'hour');
      const durationMinutes = Math.min(
        Math.max(oldEnd.diff(oldStart, 'minute'), END_TIME_SHORT_STEP_MINUTES),
        MAX_END_TIME_DURATION_MINUTES,
      );
      const newStartDateTime = date
        .startOf('day')
        .add(oldStart.hour() * 60 + oldStart.minute(), 'minute');
      const newEndDateTime = newStartDateTime.add(durationMinutes, 'minute');

      const newStart = meetingDateTimeToUtcIso(newStartDateTime, selectedTimeZone);
      const newEnd = meetingDateTimeToUtcIso(newEndDateTime, selectedTimeZone);

      formik.setFieldValue('start_time', newStart);
      formik.setFieldError('start_time', '');

      syncRecurrenceStartDate(date, oldStart);

      if (dayjs(newEnd).isSame(dayjs(newStart)) || dayjs(newEnd).isBefore(dayjs(newStart))) {
        formik.setFieldValue('end_time', null);
        formik.setFieldError('end_time', '');
        return;
      }

      formik.setFieldValue('end_time', newEnd);
      formik.setFieldError('end_time', '');
    },
    [formik.values.start_time, formik.values.end_time, selectedTimeZone, syncRecurrenceStartDate],
  );

  const handleChangeStartTime = useCallback(
    (timeValue: string | null) => {
      if (!timeValue) {
        formik.setFieldValue('start_time', null);
        formik.setFieldError('start_time', '');
        formik.setFieldValue('end_time', null);
        formik.setFieldError('end_time', '');
        return;
      }

      const nextStartMinutes = timeValueToMinutes(timeValue);
      if (nextStartMinutes === null) return;

      const previousStartDateTime = formik.values.start_time
        ? utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone)
        : getNowInMeetingTimeZone(selectedTimeZone);
      const oldEndDate = formik.values.end_time
        ? utcIsoToMeetingDateTime(formik.values.end_time, selectedTimeZone)
        : null;
      const nextStartTime = previousStartDateTime.startOf('day').add(nextStartMinutes, 'minute');
      const newStart = meetingDateTimeToUtcIso(nextStartTime, selectedTimeZone);

      formik.setFieldValue('start_time', newStart);
      formik.setFieldError('start_time', '');
      syncRecurrenceStartDate(nextStartTime, nextStartTime);

      if (oldEndDate) {
        const previousDurationMinutes = Math.min(
          Math.max(oldEndDate.diff(previousStartDateTime, 'minute'), END_TIME_SHORT_STEP_MINUTES),
          MAX_END_TIME_DURATION_MINUTES,
        );
        const durationMinutes = getNearestValidEndDurationMinutes(previousDurationMinutes);
        const newEnd = meetingDateTimeToUtcIso(
          nextStartTime.add(durationMinutes, 'minute'),
          selectedTimeZone,
        );

        formik.setFieldValue('end_time', newEnd);
        formik.setFieldError('end_time', '');
        return;
      }

      formik.setFieldValue(
        'end_time',
        meetingDateTimeToUtcIso(nextStartTime.add(60, 'minute'), selectedTimeZone),
      );
      formik.setFieldError('end_time', '');
    },
    [formik.values.start_time, formik.values.end_time, selectedTimeZone, syncRecurrenceStartDate],
  );

  const handleChangeEndTime = useCallback(
    (timeValue: string | null) => {
      if (!timeValue) {
        formik.setFieldValue('end_time', null);
        formik.setFieldError('end_time', '');
        return;
      }

      const start = formik.values.start_time
        ? utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone)
        : null;
      const nextEndMinutes = selectValueToMinutes(timeValue);

      if (!start || nextEndMinutes === null) {
        formik.setFieldValue('end_time', null);
        formik.setFieldError('end_time', '');
        return;
      }

      const nextEndTime = start.startOf('day').add(nextEndMinutes, 'minute');
      const newEnd = meetingDateTimeToUtcIso(nextEndTime, selectedTimeZone);

      if (
        formik.values.start_time &&
        (dayjs(newEnd).isSame(dayjs(formik.values.start_time)) ||
          dayjs(newEnd).isBefore(dayjs(formik.values.start_time)))
      ) {
        formik.setFieldValue('end_time', null);
        formik.setFieldError('end_time', '');
        return;
      }

      formik.setFieldValue('end_time', newEnd);
      formik.setFieldError('end_time', '');
    },
    [formik.values.start_time, selectedTimeZone],
  );

  const handleToggle = (field: string) => (value: boolean) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');

    if (field === 'is_online_meeting' && !value) {
      formik.setFieldValue('create_ai_bot', false);
      formik.setFieldError('create_ai_bot', '');
    }
  };

  const handleToggleReminders = useCallback(
    (value: boolean) => {
      setDidChangeReminders(true);
      formik.setFieldValue('reminders', value ? [{ ...defaultMeetingReminder }] : []);
      formik.setFieldError('reminders', '');
    },
    [formik.setFieldValue, formik.setFieldError],
  );

  const handleAddReminder = useCallback(() => {
    setDidChangeReminders(true);
    formik.setFieldValue('reminders', [
      ...(formik.values.reminders || []),
      { ...defaultMeetingReminder },
    ]);
    formik.setFieldError('reminders', '');
  }, [formik.values.reminders, formik.setFieldValue, formik.setFieldError]);

  const handleChangeReminder = useCallback(
    (index: number, value: Partial<MeetingReminder>) => {
      setDidChangeReminders(true);
      formik.setFieldValue(
        'reminders',
        (formik.values.reminders || []).map((reminder, reminderIndex) =>
          reminderIndex === index ? { ...reminder, ...value } : reminder,
        ),
      );
      formik.setFieldError('reminders', '');
    },
    [formik.values.reminders, formik.setFieldValue, formik.setFieldError],
  );

  const handleDeleteReminder = useCallback(
    (index: number) => {
      setDidChangeReminders(true);
      formik.setFieldValue(
        'reminders',
        (formik.values.reminders || []).filter((_, reminderIndex) => reminderIndex !== index),
      );
      formik.setFieldError('reminders', '');
    },
    [formik.values.reminders, formik.setFieldValue, formik.setFieldError],
  );

  const handleChangeUsers = useCallback(
    async (users: SelectOption<number | string, string>[] | null) => {
      const attendees: ScheduleMeetingAttendeeWithAvatar[] | undefined =
        users?.map<ScheduleMeetingAttendeeWithAvatar>(
          (item: SelectOption<number | string, string> & ScheduleMeetingAttendeeWithAvatar) => {
            if (typeof item.value === 'number') {
              return {
                user_id: item.value,
                name: item.label,
                avatar_url: item.avatar_url,
                first_name: item.first_name,
                last_name: item.last_name,
              };
            }
            return {
              email: item.value,
              name: item.label,
              avatar_url: item.avatar_url,
              first_name: item.value,
              last_name: '@',
            };
          },
        );
      await formik.setFieldValue('attendees', attendees);
      formik.setFieldError('attendees', '');
    },
    [],
  );

  const RRuleToRecurrence = useCallback(
    (rrule: string) => {
      const options = rrulestr(rrule).options;
      const pattern = RRuleFrequencyToMeetingRecurrencePatternType(options.freq);

      formik.setFieldValue('recurrence.pattern_type', pattern);
      formik.setFieldError('recurrence.pattern_type', '');

      formik.setFieldValue('recurrence.interval', options.interval);
      formik.setFieldError('recurrence.interval', '');

      formik.setFieldValue(
        'recurrence.days_of_week',
        options.byweekday?.map(item => RRuleWeekDayToMeetingDayOfWeek(item)) || null,
      );
      formik.setFieldError('recurrence.days_of_week', '');

      formik.setFieldValue('recurrence.index', undefined);
      formik.setFieldError('recurrence.index', '');
      formik.setFieldValue('recurrence.month', undefined);
      formik.setFieldError('recurrence.month', '');

      if (
        pattern === MeetingRecurrencePatternType.RelativeMonthly ||
        pattern === MeetingRecurrencePatternType.RelativeYearly
      ) {
        formik.setFieldValue('recurrence.index', MeetingRecurrenceWeekIndex.First);
        formik.setFieldError('recurrence.index', '');
      }

      if (pattern === MeetingRecurrencePatternType.RelativeYearly) {
        const currentDate = formik.values.start_time
          ? utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone)
          : getNowInMeetingTimeZone(selectedTimeZone);
        formik.setFieldValue('recurrence.month', currentDate.month() + 1);
        formik.setFieldError('recurrence.month', '');
      }

      formik.setFieldValue(
        'recurrence.end_date',
        options.until
          ? utcIsoToMeetingDateTime(dayjs(options.until).toISOString(), selectedTimeZone).format(
              'YYYY-MM-DD',
            )
          : undefined,
      );
      formik.setFieldError('recurrence.end_date', '');

      formik.setFieldValue(
        'recurrence.range_type',
        options.until ? MeetingRecurrenceRangeType.EndDate : MeetingRecurrenceRangeType.NoEnd,
      );
      formik.setFieldError('recurrence.range_type', '');

      formik.setFieldValue(
        'recurrence.start_date',
        options.dtstart
          ? utcIsoToMeetingDateTime(dayjs(options.dtstart).toISOString(), selectedTimeZone).format(
              'YYYY-MM-DD',
            )
          : undefined,
      );
      formik.setFieldError('recurrence.start_date', '');

      formik.setFieldValue('recurrence.first_day_of_week', MeetingDayOfWeek.Monday);
      formik.setFieldError('recurrence.first_day_of_week', '');
    },
    [formik.values.start_time, selectedTimeZone],
  );

  const handleChangeRepeat = useCallback(
    (value: boolean) => {
      setIsRepeat(value);
      if (value) {
        //on
        if (reCurrencyRule) {
          RRuleToRecurrence(reCurrencyRule);
          const startDateTime = formik.values.start_time
            ? utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone)
            : getNowInMeetingTimeZone(selectedTimeZone);
          syncRecurrenceStartDate(startDateTime, startDateTime, true);
          formik.setFieldError('recurrence.start_date', '');
        }
      } else {
        //off
        formik.setFieldValue('recurrence', undefined);
        formik.setFieldError('recurrence.start_date', '');
      }
    },
    [
      reCurrencyRule,
      RRuleToRecurrence,
      formik.values.start_time,
      selectedTimeZone,
      syncRecurrenceStartDate,
    ],
  );

  const RRuleHandler = useCallback(
    (rrule: string) => {
      setReCurrencyRule(rrule);
      RRuleToRecurrence(rrule);
    },
    [RRuleToRecurrence],
  );

  useEffect(() => {
    console.log('errors', formik.errors);
  }, [formik.errors]);

  return (
    <ModalContentWrapper
      onClose={onClose}
      open={open}
      title={item ? 'Update meeting' : 'New meeting'}
      classes={{ children: 'p-0' }}
    >
      <FormikProvider value={formik}>
        <div className="p-3 sm:p-6 flex flex-col w-full gap-6">
          <TextInputField
            name="subject"
            label="Meeting name"
            placeholder="Enter meeting name..."
            // limit={255}
          />

          <div className="w-full flex flex-wrap xl:flex-nowrap items-start gap-2.5">
            <div className="w-full xl:max-w-[100px]">
              <CalendarInput
                allowClear={false}
                name="Date"
                label="Date"
                format="DD-MM-YYYY"
                onChange={handleChangeDate}
                error={formik.errors?.start_time}
                value={
                  formik.values?.start_time
                    ? utcIsoToMeetingDateTime(formik.values.start_time, selectedTimeZone)
                    : null
                }
                disabledDate={disabledDateByMeetingTimeZone}
              />
            </div>

            <div className="w-full">
              <Select
                label="Start time"
                options={startTimeOptions}
                value={startTimeValue ?? undefined}
                onChange={handleChangeStartTime}
                error={formik.errors.start_time}
                isClearable={false}
              />
            </div>

            <div className="w-full">
              <Select
                label="End time"
                options={endTimeOptions}
                value={endTimeValue ?? undefined}
                onChange={handleChangeEndTime}
                error={formik.errors.end_time}
                isClearable={false}
                disabled={!startTimeValue}
              />
            </div>
          </div>
          {localDateTimeHint && (
            <div className="-mt-4 text-xs text-gray-500">{localDateTimeHint}</div>
          )}

          <div className="w-full">
            <Select
              label="Timezone"
              options={timezoneOptions}
              value={formik.values.time_zone}
              onChange={handleChangeSelect('time_zone')}
              error={formik.errors.time_zone}
              isClearable={true}
              disabled={true}
            />
          </div>

          {!formik.values.is_online_meeting && (
            <TextInputField
              name="location"
              label="Location (optional)"
              placeholder="Enter location..."
            />
          )}

          <TextInputField
            name="body"
            label="Description (optional)"
            placeholder="Enter description..."
            textarea={true}
            // limit={1000}
          />

          {formik.values.is_online_meeting && (
            <Toggle
              title="Create AI bot"
              enabled={!!formik.values.create_ai_bot}
              onChange={handleToggle('create_ai_bot')}
            />
          )}

          {formik.values.is_online_meeting && formik.values.create_ai_bot && (
            <TextInputField
              name="bot_name"
              label="AI bot name"
              placeholder="Enter AI bot name..."
            />
          )}

          {/* {formik.values.create_ai_bot && ( */}
          {/*   <Select */}
          {/*     label="Meeting template" */}
          {/*     placeholder="Select template" */}
          {/*     options={templateOptions} */}
          {/*     value={formik.values.template_id} */}
          {/*     onChange={handleChangeSelect('template_id')} */}
          {/*     error={formik.errors.template_id as string | undefined} */}
          {/*     isClearable={false} */}
          {/*     isLoading={isTemplatesLoading} */}
          {/*   /> */}
          {/* )} */}

          <Toggle
            title="Online meeting"
            enabled={!!formik.values.is_online_meeting}
            onChange={handleToggle('is_online_meeting')}
          />

          <Toggle title="Repeat" enabled={isRepeat} onChange={handleChangeRepeat} />

          {isRepeat && (
            <div className="w-full flex flex-col gap-1">
              <div className="w-3/5">
                <RRuleComponent
                  value={reCurrencyRule}
                  minEndDate={
                    formik.values.end_time
                      ? utcIsoToMeetingDateTime(formik.values.end_time, selectedTimeZone)
                      : getNowInMeetingTimeZone(selectedTimeZone)
                  }
                  onChange={RRuleHandler}
                  showYearFrequency={false}
                />
              </div>
              {localRecurrenceHint && (
                <span className="text-xs text-gray-500">{localRecurrenceHint}</span>
              )}
              {((formik.errors.recurrence as any)?.days_of_week ||
                (formik.errors.recurrence as any)?.index ||
                (formik.errors.recurrence as any)?.month ||
                (formik.errors.recurrence as any)?.start_date ||
                (formik.errors.recurrence as any)?.end_date) && (
                <span className="text-sm text-[12px] text-error-red">
                  {(formik.errors.recurrence as any)?.days_of_week ||
                    (formik.errors.recurrence as any)?.index ||
                    (formik.errors.recurrence as any)?.month ||
                    (formik.errors.recurrence as any)?.end_date ||
                    (formik.errors.recurrence as any)?.start_date}
                </span>
              )}
            </div>
          )}

          <Toggle
            title="Meeting reminders"
            enabled={!!formik.values.reminders?.length}
            onChange={handleToggleReminders}
          />

          {!!formik.values.reminders?.length && (
            <MeetingRemindersField
              reminders={formik.values.reminders}
              canAddReminder={(formik.values.reminders?.length ?? 0) < MAX_MEETING_REMINDERS}
              errors={
                formik.errors.reminders as
                  | Array<Partial<Record<keyof MeetingReminder, string>>>
                  | string
                  | undefined
              }
              onAdd={handleAddReminder}
              onChange={handleChangeReminder}
              onDelete={handleDeleteReminder}
            />
          )}

          <UsersSelectPaginated
            value={
              formik.values.attendees?.map((item: ScheduleMeetingAttendeeWithAvatar) => ({
                value: item.user_id ?? item.email ?? '',
                label: item.name,
                avatar: (
                  <Avatar
                    withBorder
                    size="sm"
                    className="border-[#C4C4C4]"
                    classNameLetters="!text-[12px]"
                    firstName={item.first_name}
                    lastName={item.last_name}
                    src={item?.avatar_url}
                  />
                ),
                avatar_url: item.avatar_url,
                first_name: item.first_name,
                last_name: item.last_name,
              })) as unknown as SelectOption<number, string>[]
            }
            label="Attendees"
            isMulti={true}
            onChange={handleChangeUsers as any}
            error={formik.errors.attendees as string}
            enableAddedEmail={true}
            enableCreation={false}
            formatCreateLabel="Invite external"
          />
        </div>
        <div className="mt-4 flex flex-col gap-4 p-3 !pb-0 sm:p-6">
          {shouldShowNotifyAttendeesToggle && (
            <Toggle
              title="Notify attendees by email"
              enabled={!!formik.values.notify_attendees}
              onChange={value => {
                formik.setFieldValue('notify_attendees', value);
                formik.setFieldError('notify_attendees', '');
              }}
            />
          )}

          {item && shouldShowUpdateNotice && (
            <div
              className={
                shouldShowForcedUpdateNotice
                  ? 'rounded bg-error-red/[.08] px-4 py-3 text-sm leading-5 text-error-red'
                  : 'rounded bg-blue-700/[.08] px-4 py-3 text-sm leading-5 text-blue-700'
              }
            >
              {updateNoticeText}
            </div>
          )}

          <Button
            type="submit"
            onClick={formik.handleSubmit}
            loading={isLoadingAdd || isLoadingUpdate}
          >
            {item ? 'Update meeting' : 'Add new meeting'}
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
